/**
 * Lo cobrado con tarjeta: cuanto se facturo y cuanto llega de verdad.
 *
 * QUE PREGUNTA CONTESTA
 * ---------------------
 * "¿Cuánto me cobró la tarjeta?" y, antes que eso, "¿cuánta plata mía todavía
 * no está?".
 *
 * Una venta con tarjeta no es plata en la caja: es plata que va a llegar, menos
 * el arancel, en la fecha de acreditacion. Por eso lo que NO se acredito
 * todavia va arriba y separado: es lo unico de esta pantalla que todavia puede
 * fallar.
 */
import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { formatearFecha } from "@/lib/utils";
import { aNumero, type FilaLiquidacionTarjeta, type TotalMoneda } from "@/lib/tipos";

const COLUMNAS = 7;

export interface TarjetasProps {
  filas: ReadonlyArray<FilaLiquidacionTarjeta>;
  /** El NETO por moneda del período, del servidor. */
  totales?: { ars: TotalMoneda; usd: TotalMoneda };
}

export function Tarjetas({ filas, totales }: TarjetasProps): JSX.Element {
  // Sin acreditar primero: es lo que todavia puede no llegar. Dentro de cada
  // grupo se respeta el orden que vino (por fecha de acreditacion).
  const pendientes = filas.filter((f) => !f.acreditado);
  const acreditadas = filas.filter((f) => f.acreditado);

  return (
    <Card padding="none" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Table densidad="compacta" alto="lleno">
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>Tarjeta</TableHead>
            <TableHead className="hidden sm:table-cell">Cuenta</TableHead>
            <TableHead className="text-right">Bruto</TableHead>
            <TableHead className="text-right">Arancel</TableHead>
            <TableHead className="text-right">Neto</TableHead>
            <TableHead>Se acredita</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.length === 0 ? (
            <TableVacia colSpan={COLUMNAS} mensaje="En este período no se cobró con tarjeta." />
          ) : (
            <>
              {pendientes.length > 0 ? (
                <>
                  <Separador texto={`Todavía no acreditadas (${pendientes.length})`} />
                  {pendientes.map((f) => (
                    <FilaTarjeta key={String(f.id)} fila={f} />
                  ))}
                </>
              ) : null}
              {acreditadas.length > 0 ? (
                <>
                  {pendientes.length > 0 ? (
                    <Separador texto={`Ya acreditadas (${acreditadas.length})`} />
                  ) : null}
                  {acreditadas.map((f) => (
                    <FilaTarjeta key={String(f.id)} fila={f} />
                  ))}
                </>
              ) : null}
            </>
          )}
        </TableBody>
        {filas.length > 0 && totales ? (
          <TableFooter>
            {totales.ars.cantidad > 0 ? (
              <FilaTotal etiqueta="Neto en pesos" moneda="ARS" total={totales.ars} />
            ) : null}
            {totales.usd.cantidad > 0 ? (
              <FilaTotal etiqueta="Neto en dólares" moneda="USD" total={totales.usd} />
            ) : null}
          </TableFooter>
        ) : null}
      </Table>
    </Card>
  );
}

/** Una fila de titulo adentro de la tabla: separa lo que falta de lo que llegó. */
function Separador({ texto }: { texto: string }): JSX.Element {
  return (
    <TableRow>
      <TableCell colSpan={COLUMNAS} className="bg-muted">
        <TituloSeccion nivel="etiqueta" as="span">
          {texto}
        </TituloSeccion>
      </TableCell>
    </TableRow>
  );
}

function FilaTarjeta({ fila }: { fila: FilaLiquidacionTarjeta }): JSX.Element {
  const neto = fila.neto === null ? aNumero(fila.bruto) : aNumero(fila.neto);
  const arancelPct = aNumero(fila.arancel_pct);

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap tabular-nums">{formatearFecha(fila.fecha)}</TableCell>
      <TableCell className="whitespace-nowrap text-sm">
        {fila.tarjeta_marca ?? "—"}
        {fila.cuotas && fila.cuotas > 1 ? (
          <span className="pl-1 text-xs tabular-nums text-muted-foreground">
            en {fila.cuotas} cuotas
          </span>
        ) : null}
      </TableCell>
      <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
        {fila.cuenta}
      </TableCell>
      <TableCellImporte monto={aNumero(fila.bruto)} moneda={fila.moneda} tamano="sm" />
      <TableCell className="text-right">
        <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
          <Importe monto={-aNumero(fila.arancel)} moneda={fila.moneda} tamano="sm" codigo={false} />
          {arancelPct > 0 ? (
            <span className="text-xs tabular-nums text-muted-foreground">({arancelPct}%)</span>
          ) : null}
        </span>
      </TableCell>
      <TableCellImporte monto={neto} moneda={fila.moneda} tamano="sm" />
      <TableCell className="whitespace-nowrap">
        {fila.acreditado ? (
          <Badge variant="success">ACREDITADA</Badge>
        ) : (
          <span className="text-sm tabular-nums text-muted-foreground">
            {fila.fecha_acreditacion ? formatearFecha(fila.fecha_acreditacion) : "Sin fecha"}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function FilaTotal({
  etiqueta,
  moneda,
  total,
}: {
  etiqueta: string;
  moneda: "ARS" | "USD";
  total: TotalMoneda;
}): JSX.Element {
  return (
    <TableRow>
      <TableCell colSpan={COLUMNAS - 2}>
        {etiqueta}
        <span className="pl-2 font-normal tabular-nums text-muted-foreground">
          {total.cantidad} {total.cantidad === 1 ? "operación" : "operaciones"}
        </span>
      </TableCell>
      <TableCellImporte monto={aNumero(total.importe)} moneda={moneda} />
      <TableCell />
    </TableRow>
  );
}
